import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { UpvoteButton, DownvoteButton } from './VoteButtons';
import { notify } from '../reducers/notificationReducer';
import EditDeleteMenu from './EditDeleteMenu';
import getEditedThumbail from '../utils/cloudinaryTransform';
import { trimLink, prettifyLink, fixUrl } from '../utils/formatUrl';
import TimeAgo from 'timeago-react';
import getErrorMsg from '../utils/getErrorMsg';

import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Link,
  Button,
  useMediaQuery
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import MessageIcon from '@material-ui/icons/Message';
import LinkIcon from '@material-ui/icons/Link';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CommentIcon from '@material-ui/icons/Comment';

const PostCard = ({ post, toggleUpvote, toggleDownvote }) => {
  const {
    id, title, postType, textSubmission, linkSubmission,
    imageSubmission, peekgroup, author, upvotedBy, downvotedBy,
    pointsCount, commentCount, createdAt, updatedAt
  } = post;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const dispatch = useDispatch();
  const { user, darkMode } = useSelector((state) => state);

  const isUpvoted = user && upvotedBy.includes(user.id);
  const isDownvoted = user && downvotedBy.includes(user.id);

  const handleVote = async (isUp) => {
    try {
      const isVoted = isUp ? isUpvoted : isDownvoted;
      const updatedUp = isUpvoted && !isUp ? upvotedBy.filter(u => u !== user.id) : [...upvotedBy, user.id].filter(u => u !== (isUp ? null : user.id));
      const updatedDown = isDownvoted && isUp ? downvotedBy.filter(d => d !== user.id) : [...downvotedBy, user.id].filter(d => d !== (isUp ? user.id : null));
      dispatch(isUp ? toggleUpvote(id, updatedUp, updatedDown) : toggleDownvote(id, updatedDown, updatedUp));
    } catch (err) {
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };

  const linkToShow = postType === 'Link' ? linkSubmission : postType === 'Image' ? imageSubmission.imageLink : '';
  const formattedLink = trimLink(prettifyLink(linkToShow), 30);

  return (
    <Card
      elevation={3}
      style={{
        borderRadius: 16,
        backgroundColor: darkMode ? '#1E1E1E' : '#fff',
        display: 'flex',
        padding: '22px',
        gap: '16px',
        alignItems: 'flex-start',
        position: 'relative'
      }}
    >
      {/* Voting Column */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 48,
      }}>
        <UpvoteButton
          user={user}
          body={post}
          handleUpvote={() => handleVote(true)}
          size={isMobile ? 'small' : 'medium'}
        />
        <Typography variant="body2" style={{
          fontWeight: 'bold',
          color: isUpvoted
            ? '#FF8b60'
            : isDownvoted
            ? '#9494FF'
            : darkMode
            ? '#e4e4e4'
            : '#333'
        }}>
          {pointsCount}
        </Typography>
        <DownvoteButton
          user={user}
          body={post}
          handleDownvote={() => handleVote(false)}
          size={isMobile ? 'small' : 'medium'}
        />
      </div>

      {/* Thumbnail / Image */}
      <div style={{ minWidth: 80, height: 80, borderRadius: 12, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {postType === 'Text' ? (
          <RouterLink to={`/comments/${id}`}>
            <MessageIcon style={{ fontSize: 32, color: '#787878' }} />
          </RouterLink>
        ) : postType === 'Link' ? (
          <a href={fixUrl(linkSubmission)} target="_blank" rel="noopener noreferrer">
            <LinkIcon style={{ fontSize: 32, color: '#787878' }} />
          </a>
        ) : (
          <a href={imageSubmission.imageLink} target="_blank" rel="noopener noreferrer">
            <CardMedia
              component="img"
              image={getEditedThumbail(imageSubmission.imageLink)}
              alt={title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </a>
        )}
      </div>

      {/* Post Info */}
      <CardContent style={{ padding: 3, flex: 1 }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 4}}>
          {title}
          {postType !== 'Text' && (
            <Link href={postType === 'Link' ? fixUrl(linkSubmission) : imageSubmission.imageLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', fontSize: '0.8rem', color: 'lightgray' }}
            >
              {formattedLink}
              <OpenInNewIcon fontSize="small" style={{ marginLeft: 4 }} />
            </Link>
          )}
        </Typography>

        <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
          <Link component={RouterLink} to={`/p/${peekgroup.peekgroupName}`}>
            p/{peekgroup.peekgroupName}
          </Link>{' '}
          • Posted by <Link component={RouterLink} to={`/u/${author.username}`}>u/{author.username}</Link> {' '}
          • <TimeAgo datetime={new Date(createdAt)} />
          {createdAt !== updatedAt && ' *'}
        </Typography>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            startIcon={<CommentIcon />}
            component={RouterLink}
            to={`/comments/${id}`}
            size={isMobile ? 'small' : 'medium'}
            style={{
              borderRadius: 20,
              padding: '4px 12px',
              textTransform: 'none'
            }}
          >
            {commentCount} comments
          </Button>

          {user?.id === author.id && (
            <EditDeleteMenu
              id={id}
              isMobile={isMobile}
              title={title}
              postType={postType}
              peekgroup={peekgroup}
              textSubmission={textSubmission}
              linkSubmission={linkSubmission}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PostCard;
